import {
  faFileExport,
  faHome,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  Row,
} from '@themesberg/react-bootstrap';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import Table from 'react-bootstrap-table-next';
import Pagination from 'react-bootstrap-table2-paginator';
import * as Paginator from 'react-bootstrap-table2-paginator';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Routes } from '../../router/routes';
import { updateSpinnerState } from '../../actions/spinner';
import { updateToastInfo } from '../../actions/settings';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import { setFiltersInStore } from '../../actions/filters';
import { returnEndOfDay, returnStartOfDay } from '../../utils/datetime';
import { returnWarehouseDropdownOptions } from '../../utils/warehouse';
import EditableDropDown from '../../components/common/EditableDropdown';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import ExcelDownloadReport from '../../components/common/excel/download';
import { getWarehousePendingDelivery } from '../../parse-functions/trips';
import {
  customSizePerPage,
  customTotal,
} from '../../components/common/parcelStatusPages';
import EmptyTable from '../../components/common/EmptyTableView';
import { returnUserReadableDateTime } from '../../utils/datetime';

// Table columns configuration
const getPendingDeliveryColumns = () => [
  {
    dataField: 'invoiceNo',
    text: 'Invoice No',
    sort: true,
    headerStyle: { width: '120px' },
    formatter: (cell) => cell?.replace(/\r\n/g, ' ').trim(),
  },
  {
    dataField: 'date',
    text: 'Date',
    sort: true,
    headerStyle: { width: '120px' },
    formatter: (cell) => {
      if (cell?.iso) {
        return returnUserReadableDateTime(new Date(cell.iso));
      }
      return returnUserReadableDateTime(new Date(cell));
    },
  },
  {
    dataField: 'warehouseCode',
    text: 'Branch',
    sort: true,
    headerStyle: { width: '150px' },
  },
  {
    dataField: 'targetBranch',
    text: 'Target Branch',
    sort: true,
    headerStyle: { width: '150px' },
  },
  {
    dataField: 'customerName',
    text: 'Customer Name',
    sort: true,
    headerStyle: { width: '200px' },
    formatter: (cell) => cell?.replace(/\r\n/g, ' ').trim(),
  },
  {
    dataField: 'contactNumber',
    text: 'Contact Number',
    sort: true,
    headerStyle: { width: '120px' },
    formatter: (cell) => `${cell}`,
  },
  {
    dataField: 'product',
    text: 'Product',
    sort: true,
    headerStyle: { width: '200px' },
    formatter: (cell) => cell?.replace(/\r\n/g, ' ').trim(),
  },
  {
    dataField: 'quantity',
    text: 'Quantity',
    sort: true,
    headerStyle: { width: '100px' },
  },
  {
    dataField: 'status',
    text: 'Status',
    sort: true,
    headerStyle: { width: '100px' },
    formatter: (cell) => (
      <span className={`badge bg-${cell === 'pending' ? 'warning' : 'success'}`}>
        {cell?.toUpperCase()}
      </span>
    ),
  },
];

const PendingWarehouseDeliveryReport = () => {
  const { t } = useTranslation();
  const pageType = 'pendingDelivery';
  const dispatch = useDispatch();
  const filtersValue = useSelector((state) => state.filters?.[pageType]);
  const [formInput, setFormInput] = useState({
    storeName: filtersValue?.storeName,
    region: filtersValue?.region,
  });
  const zones = useSelector((state) => state?.user?.config?.zones || {});
  const storeInfo = useSelector((state) => state.user.warehouse || {});
  const [dataToDownload, setDataToDownload] = useState([]);
  const [reportData, setReportData] = useState([]);
  const isSuperAdmin = useSelector(
    (state) => state.user.preferences.isSuperAdmin || false
  );

  const otherWarehouses = useSelector(
    (state) => state?.user?.otherWarehousesInZone || {}
  );

  const onDateSelect = (startDate, endDate) => {
    dispatch(
      setFiltersInStore({
        pageType,
        data: {
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          storeName: formInput.storeName,
          region: formInput.region,
        },
      })
    );
    // API call will be implemented here
    console.log('Date range selected:', {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      storeName: formInput.storeName?.[0],
      region: formInput.region?.[0],
      isSuperAdmin,
    });
    handleGetReport();
  };

  const handleGetReport = async () => {
    const requestProps = {
      startDate: filtersValue?.startDate
        ? returnStartOfDay(new Date(filtersValue?.startDate))
        : returnStartOfDay(new Date()),
      endDate: filtersValue?.endDate
        ? returnEndOfDay(new Date(filtersValue?.endDate))
        : returnEndOfDay(new Date()),
      warehouseCode: !isSuperAdmin
        ? storeInfo?.warehouseCode
        : formInput.storeName?.[0],
      region: isSuperAdmin && formInput.region?.[0],
      isSuperAdmin,
    };
    
    dispatch(updateSpinnerState(true));
    
    try {
      const response = await getWarehousePendingDelivery(requestProps);
      
      // Use the response directly as the data
    //   const data = Array.isArray(response) ? response : [];
      const { data } = response;
      setReportData(data);
      setDataToDownload(data);
      
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'success',
          title: t('Report Generated'),
          message: t('Pending delivery report has been generated successfully') + ` (${data.length} records)`,
        })
      );
    } catch (error) {
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t('Error generating report'),
          message: error.message || t('Failed to generate pending delivery report'),
        })
      );
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <Breadcrumb
            className="d-none d-md-inline-block"
            listProps={{ className: 'breadcrumb-dark breadcrumb-transparent' }}
          >
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item>{t('Analysis')}</Breadcrumb.Item>
            <Breadcrumb.Item active>{t('Pending Delivery')}</Breadcrumb.Item>
          </Breadcrumb>
          <h4>{t('Pending Delivery Report')}</h4>
          <p className="mb-0">
            {t('Generate and view pending delivery reports for selected date range and location.')}
          </p>
        </div>

        <div className="btn-toolbar mb-2 mb-md-0">
          <ButtonGroup>
            <Button
              onClick={() => {
                handleGetReport();
              }}
              variant="outline-primary"
              size="sm"
            >
              <FontAwesomeIcon icon={faRefresh} />
              &nbsp;&nbsp;{t('Generate Report')}
            </Button>
            <ExcelDownloadReport
              data={dataToDownload || []}
              fileName={`Pending-Delivery-Report.csv`}
            />
          </ButtonGroup>
        </div>
      </div>

      <div className="table-settings mb-4">
        <div className="mb-2 d-flex gap-4">
          {isSuperAdmin && (
            <div className="d-flex gap-4">
              <Form.Group>
                <Form.Label>{t('Region')}</Form.Label>
                <EditableDropDown
                  id="region"
                  onChange={(e) =>
                    setFormInput((prev) => ({
                      ...prev,
                      region: [e.value],
                    }))
                  }
                  value={formInput.region}
                  options={[
                    { label: 'All', value: 'All' },
                    ...arrayToDropdownOptions(Object.keys(zones)),
                  ]}
                  placeholder="Choose Region"
                  backgroundColor="#F0F0F0"
                  width={'250px'}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>{t('Store Name')}</Form.Label>
                <EditableDropDown
                  id="warehouseCode"
                  onChange={(e) =>
                    setFormInput((prev) => ({
                      ...prev,
                      storeName: [e.value],
                    }))
                  }
                  value={formInput.storeName}
                  options={[
                    { label: 'All', value: 'All' },
                    ...returnWarehouseDropdownOptions(otherWarehouses),
                  ]}
                  placeholder="Choose Store Name"
                  backgroundColor="#F0F0F0"
                  width={'250px'}
                />
              </Form.Group>
            </div>
          )}
          <div className="d-flex gap-4">
            <div className="mt-4">
              <DateRangeSelector
                onSubmit={onDateSelect}
                filtersValue={filtersValue}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Report Table */}
      {reportData.length > 0 ? (
        <ToolkitProvider
          exportCSV
          keyField="objectId"
          search={true}
          sort
          columns={getPendingDeliveryColumns()}
          data={reportData}
          wrapperClasses="table-responsive"
        >
          {({ baseProps, searchProps, csvProps }) => (
            <Paginator.PaginationProvider
              pagination={Pagination({
                custom: true,
                showTotal: true,
                alwaysShowAllBtns: true,
                totalSize: reportData.length,
                paginationTotalRenderer: customTotal,
                sizePerPageRenderer: customSizePerPage,
              })}
            >
              {({ paginationProps, paginationTableProps }) => (
                <Card>
                  <Card.Header>
                    <Row className="d-flex justify-content-between">
                      <Col xs={8} md={6} className="d-flex py-1">
                        <h5 className="mb-0">{t('Pending Delivery Report')}</h5>
                      </Col>
                      <Col
                        xs={4}
                        md={4}
                        className="d-flex justify-content-md-end py-1"
                      >
                        <Search.SearchBar
                          {...searchProps}
                          placeholder={t('Search any value in table here')}
                        />
                        <Search.ClearSearchButton {...searchProps} />
                      </Col>
                    </Row>
                  </Card.Header>
                  <div className="table-responsive">
                    <Table
                      noDataIndication={<EmptyTable />}
                      {...baseProps}
                      {...paginationTableProps}
                      bodyClasses="border-0"
                      classes="table-flush dataTable-table"
                    />
                  </div>
                  <Card.Footer>
                    <Row>
                      <Col xs={12} md={4} className="py-1">
                        <Paginator.SizePerPageDropdownStandalone
                          {...paginationProps}
                          paginationSize={5}
                          sizePerPageList={[{ text: '5', value: 5 }]}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        className="d-flex align-items-center py-1"
                      >
                        <Paginator.PaginationTotalStandalone
                          {...paginationProps}
                        />
                      </Col>
                      <Col
                        xs={12}
                        md={4}
                        className="d-flex justify-content-md-end align-items-center mb-0 py-1"
                      >
                        <Paginator.PaginationListStandalone
                          {...paginationProps}
                        />
                      </Col>
                    </Row>
                  </Card.Footer>
                </Card>
              )}
            </Paginator.PaginationProvider>
          )}
        </ToolkitProvider>
      ) : (
        <Card>
          <Card.Body>
            <h5 className="card-title">{t('Pending Delivery Report')}</h5>
            <p className="card-text">
              {t('Select date range and filters above to generate the pending delivery report.')}
            </p>
            <div className="text-center py-5">
              <p className="text-muted">
                {t('No data available. Please select date range and generate report.')}
              </p>
            </div>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default PendingWarehouseDeliveryReport;
